import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { categoryService, CategoryWithImage } from '../../services/categoryService';

const CategoryImageManagement: React.FC = () => {
    const { user } = useAuth();
    const [categories, setCategories] = useState<CategoryWithImage[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<CategoryWithImage | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const [editingCategory, setEditingCategory] = useState<CategoryWithImage | null>(null);
    const [editForm, setEditForm] = useState({ name: '', description: '' });
    const fileInputRef = useRef<HTMLInputElement>(null);
    const uploadSectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        try {
            setLoading(true);
            const response = await categoryService.getCategoriesAdmin();
            setCategories(response.categories);
        } catch (error: any) {
            console.error('Erreur lors du chargement des catégories:', error);
        } finally {
            setLoading(false);
        }
    };

    // Check admin access
    if (user?.role !== 'admin') {
        return (
            <div className="min-h-screen bg-eldai-gray-light flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-md text-center">
                    <h1 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h1>
                    <p className="text-eldai-gray">Vous n'avez pas les permissions pour accéder à cette page.</p>
                    <Link to="/" className="btn btn-primary mt-4 inline-block">
                        Retour à l'accueil
                    </Link>
                </div>
            </div>
        );
    }

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            handleFileSelect(e.target.files[0]);
        }
    };

    const handleFileSelect = (file: File) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Veuillez sélectionner un fichier image valide');
            return;
        }

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            alert('Le fichier ne doit pas dépasser 5MB');
            return;
        }

        setSelectedFile(file);
    };

    const handleUpload = async () => {
        if (!selectedFile || !selectedCategory) {
            alert('Veuillez sélectionner une catégorie et un fichier');
            return;
        }

        try {
            setUploading(true);
            await categoryService.uploadCategoryImage(selectedCategory.id, selectedFile);

            // Reload categories to show updated image
            await loadCategories();

            // Reset form
            setSelectedFile(null);
            setSelectedCategory(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

            alert('Image uploadée avec succès!');
        } catch (error: any) {
            console.error('Erreur upload:', error);
            alert(error.message || 'Erreur lors de l\'upload');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteImage = async (category: CategoryWithImage) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'image de la catégorie "${category.name}" ?`)) {
            return;
        }

        try {
            await categoryService.deleteCategoryImage(category.id);
            await loadCategories();
            alert('Image supprimée avec succès!');
        } catch (error: any) {
            console.error('Erreur suppression:', error);
            alert(error.message || 'Erreur lors de la suppression');
        }
    };

    const handleEditCategory = (category: CategoryWithImage) => {
        setEditingCategory(category);
        setEditForm({
            name: category.name,
            description: category.description || ''
        });
    };

    const handleSaveEdit = async () => {
        if (!editingCategory) return;

        try {
            await categoryService.updateCategory(editingCategory.id, editForm);
            await loadCategories();
            setEditingCategory(null);
            alert('Catégorie mise à jour avec succès!');
        } catch (error: any) {
            console.error('Erreur mise à jour:', error);
            alert(error.message || 'Erreur lors de la mise à jour');
        }
    };

    const formatFileSize = (bytes: number) => {
        const mb = bytes / (1024 * 1024);
        return mb.toFixed(2) + ' MB';
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getCategoryImageUrl = (category: CategoryWithImage): string | null => {
        if (category.s3_image) {
            return category.s3_image.url;
        }
        return category.image_url || null;
    };

    return (
        <div className="admin-container">
            <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center mb-4">
                        <Link to="/admin" className="admin-nav-back">
                            <span className="admin-nav-back-icon">←</span> Retour au tableau de bord
                        </Link>
                    </div>
                    <div className="admin-header">
                        <div>
                            <h1 className="admin-title">
                                <span className="admin-title-icon">🏷️</span> Gestion des images de catégories
                            </h1>
                            <p className="admin-subtitle">
                                Gérez les images des catégories affichées dans le carousel de la page d'accueil ({categories.length} catégories)
                            </p>
                        </div>
                    </div>
                </div>

                {/* Instructions */}
                <div className="admin-instructions">
                    <h3 className="admin-instructions-title">
                        📋 Instructions d'utilisation
                    </h3>
                    <div className="admin-instructions-grid">
                        <div className="admin-instructions-section">
                            <h4>📤 Upload d'images</h4>
                            <ul>
                                <li>Formats supportés: JPG, PNG, GIF, WebP</li>
                                <li>Taille maximale: 5MB par image</li>
                                <li>Résolution recommandée: 800x600 (ratio 4:3)</li>
                                <li>Une image par catégorie</li>
                            </ul>
                        </div>
                        <div className="admin-instructions-section">
                            <h4>🔄 Gestion des images</h4>
                            <ul>
                                <li>Remplacez une image en uploadant une nouvelle</li>
                                <li>Supprimez les images inutilisées</li>
                                <li>Les images sont stockées sur AWS S3</li>
                                <li>Cache automatique pour de meilleures performances</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Upload Section */}
                <div className="admin-card" ref={uploadSectionRef}>
                    <div className="admin-card-header">
                        <h2 className="admin-card-title">📤 Ajouter/Modifier une image de catégorie</h2>
                    </div>
                    <div className="admin-card-body">
                        {/* Category Selection */}
                        <div className="mb-6">
                            <label className="admin-form-label">Sélectionner une catégorie</label>
                            <select
                                value={selectedCategory?.id || ''}
                                onChange={(e) => {
                                    const categoryId = parseInt(e.target.value);
                                    const category = categories.find(c => c.id === categoryId);
                                    setSelectedCategory(category || null);
                                }}
                                className="admin-form-input"
                            >
                                <option value="">-- Choisir une catégorie --</option>
                                {categories.map((category) => (
                                    <option key={category.id} value={category.id}>
                                        {category.name} {category.has_s3_image ? '(✅ A une image)' : '(❌ Pas d\'image)'}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* File Upload */}
                        {selectedCategory && (
                            <>
                                <div
                                    className={`carousel-drag-area ${dragActive ? 'active' : ''} rounded-lg p-8 text-center transition-colors mb-6`}
                                    onDragEnter={handleDrag}
                                    onDragLeave={handleDrag}
                                    onDragOver={handleDrag}
                                    onDrop={handleDrop}
                                >
                                    <div className="text-6xl mb-4">🏷️</div>
                                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                        Glissez-déposez l'image de la catégorie "{selectedCategory.name}" ici
                                    </h3>
                                    <p className="text-gray-600 mb-4">
                                        Formats acceptés: JPG, PNG, GIF, WebP • Taille max: 5MB
                                    </p>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileInputChange}
                                        className="hidden"
                                    />
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        className="admin-btn admin-btn-primary"
                                    >
                                        🖼️ Sélectionner un fichier
                                    </button>
                                </div>

                                {/* Selected File Preview */}
                                {selectedFile && (
                                    <div className="mb-6">
                                        <div className="max-w-md mx-auto">
                                            <div className="aspect-w-4 aspect-h-3 mb-4">
                                                <img
                                                    src={URL.createObjectURL(selectedFile)}
                                                    alt="Aperçu"
                                                    className="w-full h-64 object-cover rounded-lg"
                                                />
                                            </div>
                                            <p className="text-center text-sm text-gray-600">
                                                {selectedFile.name} • {formatFileSize(selectedFile.size)}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Action Buttons */}
                                {selectedFile && (
                                    <div className="flex gap-4 justify-center">
                                        <button
                                            onClick={handleUpload}
                                            disabled={uploading}
                                            className="admin-btn admin-btn-success"
                                        >
                                            {uploading ? '⏳ Upload en cours...' : '🚀 Uploader l\'image'}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setSelectedFile(null);
                                                if (fileInputRef.current) fileInputRef.current.value = '';
                                            }}
                                            className="admin-btn admin-btn-secondary"
                                        >
                                            ❌ Annuler
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Categories List */}
                <div className="admin-card">
                    <div className="admin-card-header">
                        <h2 className="admin-card-title">
                            🏷️ Catégories et leurs images ({categories.length})
                        </h2>
                    </div>

                    {loading ? (
                        <div className="admin-loading">
                            <div className="admin-loading-spinner"></div>
                            <div className="admin-loading-text">Chargement...</div>
                        </div>
                    ) : (
                        <div className="admin-card-body">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {categories.map((category) => {
                                    const imageUrl = getCategoryImageUrl(category);
                                    return (
                                        <div key={category.id} className="border rounded-lg p-4 bg-white shadow-sm">
                                            {/* Category Image */}
                                            <div className="aspect-w-4 aspect-h-3 mb-4">
                                                {imageUrl ? (
                                                    <img
                                                        src={imageUrl}
                                                        alt={category.name}
                                                        className="w-full h-48 object-cover rounded-lg"
                                                        loading="lazy"
                                                    />
                                                ) : (
                                                    <div className="w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                                                        <div className="text-center">
                                                            <span className="text-4xl mb-2 block">🏷️</span>
                                                            <span className="text-gray-500 text-sm">Pas d'image</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Category Info */}
                                            <div className="mb-4">
                                                {editingCategory?.id === category.id ? (
                                                    <div className="space-y-3">
                                                        <input
                                                            type="text"
                                                            value={editForm.name}
                                                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                                            className="w-full px-3 py-2 border rounded"
                                                            placeholder="Nom de la catégorie"
                                                        />
                                                        <textarea
                                                            value={editForm.description}
                                                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                                            className="w-full px-3 py-2 border rounded"
                                                            placeholder="Description"
                                                            rows={2}
                                                        />
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={handleSaveEdit}
                                                                className="admin-btn admin-btn-small admin-btn-success"
                                                            >
                                                                ✅ Sauvegarder
                                                            </button>
                                                            <button
                                                                onClick={() => setEditingCategory(null)}
                                                                className="admin-btn admin-btn-small admin-btn-secondary"
                                                            >
                                                                ❌ Annuler
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <h4 className="font-semibold text-lg mb-2" title={category.name}>
                                                            {category.name}
                                                        </h4>
                                                        {category.description && (
                                                            <p className="text-sm text-gray-600 mb-2">
                                                                {category.description}
                                                            </p>
                                                        )}
                                                        <div className="text-xs text-gray-500">
                                                            <p>ID: {category.id}</p>
                                                            {category.s3_image && (
                                                                <>
                                                                    <p>Taille: {formatFileSize(category.s3_image.size)}</p>
                                                                    <p>Modifié: {formatDate(category.s3_image.lastModified)}</p>
                                                                </>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            {/* Actions */}
                                            {editingCategory?.id !== category.id && (
                                                <div className="flex gap-2 flex-wrap">
                                                    <button
                                                        onClick={() => {
                                                            setSelectedCategory(category);
                                                            // Scroll to upload section with smooth animation
                                                            uploadSectionRef.current?.scrollIntoView({
                                                                behavior: 'smooth',
                                                                block: 'start'
                                                            });
                                                        }}
                                                        className="admin-btn admin-btn-small admin-btn-primary"
                                                        title="Changer l'image"
                                                    >
                                                        📤 {imageUrl ? 'Remplacer' : 'Ajouter'} image
                                                    </button>

                                                    <button
                                                        onClick={() => handleEditCategory(category)}
                                                        className="admin-btn admin-btn-small admin-btn-secondary"
                                                        title="Modifier la catégorie"
                                                    >
                                                        ✏️ Modifier
                                                    </button>

                                                    {imageUrl && (
                                                        <button
                                                            onClick={() => handleDeleteImage(category)}
                                                            className="admin-btn admin-btn-small admin-btn-danger"
                                                            title="Supprimer l'image"
                                                        >
                                                            🗑️ Supprimer image
                                                        </button>
                                                    )}

                                                    <a
                                                        href={`/catalog?categories=${category.id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="admin-btn admin-btn-small admin-btn-secondary"
                                                        title="Voir les produits de cette catégorie"
                                                    >
                                                        👁️ Voir catalogue
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CategoryImageManagement;
